import React, {ReactNode, useCallback, useContext} from "react";
import smartTriggerConfigurationReducer, {SmartTriggerConfigurationActionType} from "./smartTriggerConfigurationReducer";
import {Edge} from "reactflow";
import {SmartTriggerProcessorNodeConfiguration, SmartTriggerUiConfiguration} from "../types";


export type SmartTriggerProviderProps = {
    children: ReactNode,
    configuration: SmartTriggerUiConfiguration
}

export type SetStateAction<T> = (args: T) => T

export type SmartTriggerConfigurationManager = SmartTriggerUiConfiguration & {
    setNodes: (node: SmartTriggerProcessorNodeConfiguration[] | SetStateAction<SmartTriggerProcessorNodeConfiguration[]>) => void,
    setEdges: (edge: Edge[] | SetStateAction<Edge[]>) => void,
    setConfiguration: (config: Partial<SmartTriggerUiConfiguration> | SetStateAction<SmartTriggerUiConfiguration>) => void,
    addNewProcessor: (node: SmartTriggerProcessorNodeConfiguration) => void,
    updateNode: (node: SmartTriggerProcessorNodeConfiguration) => void,
    removeProcessor: (id: string) => void
}

const SmartTriggerConfigurationContext = React.createContext({} as SmartTriggerConfigurationManager);

export function SmartTriggerConfigurationProvider({children, configuration}: SmartTriggerProviderProps) {
    const [state, dispatch] = React.useReducer(smartTriggerConfigurationReducer, configuration);
    const value = {
        name: state.name,
        id: state.id,
        description: state.description,
        author: state.author,
        edges: state.edges,
        nodes: state.nodes,
        activeStatus: state.activeStatus,
        setNodes: useCallback((nodes: SmartTriggerProcessorNodeConfiguration[] | SetStateAction<SmartTriggerProcessorNodeConfiguration[]>) => {
            if (typeof nodes === 'function') {
                nodes = nodes(state.nodes);
            }
            dispatch({
                type: SmartTriggerConfigurationActionType.UPDATE_NODES,
                payload: {nodes}
            });
        }, [state.nodes]),
        setEdges: useCallback((edges: Edge[] | SetStateAction<Edge[]>) => {
            if (typeof edges === 'function') {
                edges = edges(state.edges);
            }
            dispatch({
                type: SmartTriggerConfigurationActionType.UPDATE_EDGES,
                payload: {edges}
            });
        }, [state.edges]),
        setConfiguration: useCallback((con: Partial<SmartTriggerUiConfiguration> | SetStateAction<SmartTriggerUiConfiguration>) => {
            if (typeof con === 'function') {
                con = con(state);
            }
            dispatch({
                type: SmartTriggerConfigurationActionType.UPDATE_CONFIGURATION,
                payload: con
            });
        }, [state]),
        addNewProcessor: (node: SmartTriggerProcessorNodeConfiguration) => {
            dispatch({
                type: SmartTriggerConfigurationActionType.ADD_NODE,
                payload: node
            })
        },
        removeProcessor(id: string) {
            dispatch({
                type: SmartTriggerConfigurationActionType.REMOVE_NODE,
                payload: {
                    id
                }
            })
        },
        updateNode(node: SmartTriggerProcessorNodeConfiguration) {
            dispatch({
                type: SmartTriggerConfigurationActionType.UPDATE_NODE,
                payload: node
            })
        }
    }

    return (
        <SmartTriggerConfigurationContext.Provider value={value}>
            {children}
        </SmartTriggerConfigurationContext.Provider>
    );
}

export function useSmartTriggerConfiguration() {
    return useContext(SmartTriggerConfigurationContext);
}
